import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./ScholarshipTestimonials.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import HorizontalScroll from "@Components/HorizontalScroll";
import Image from "@Components/Image";
import YoutubePlayIcon from "@Icons/YoutubePlayIcon";
import { getScholarshipEditionsData, type EditionData } from "constants/scholarshipConstants";
import useTranslationHook from "hooks/useTranslationHook";

/**
 * Returns the actual scrollable element inside the HorizontalScroll component.
 * HorizontalScroll renders: .hrContainer > .horizontalScroll (the scroll div).
 * CSS modules mangle class names, so we find the first child with overflow-x scroll/auto.
 */
const getScrollElement = (container: HTMLElement | null): Element | null => {
  if (!container) return null;
  const el = container.querySelector('[class*="horizontalScroll"]');
  return el;
};

const YOUTUBE_THUMB_URL = "https://img.youtube.com/vi";

const VideoCard = ({ videoId, t }: { videoId: string; t: (key: string) => string }) => {
  const [playing, setPlaying] = useState(false);

  return (
    <div className={styles.videoCard}>
      <div className={styles.videoWrapper}>
        {playing ? (
          <iframe
            className={styles.videoFrame}
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
            title={t("scholarship.testimonials.winnerstory")}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            className={styles.thumbnailButton}
            onClick={() => setPlaying(true)}
            aria-label={t("scholarship.testimonials.playvideo")}
          >
            <Image
              src={`${YOUTUBE_THUMB_URL}/${videoId}/hqdefault.jpg`}
              alt={t("scholarship.testimonials.winnerstory")}
              className={styles.thumbnailImg}
              height="auto"
              width="auto"
            />
            <div className={styles.playOverlay}>
              <YoutubePlayIcon />
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

const EditionCard = ({ edition, t }: { edition: EditionData; t: (key: string) => string }) => (
  <div className={styles.editionCard}>
    <div className={styles.videosGrid}>
      {edition.videos.map((video, index) => (
        <VideoCard key={index} videoId={video.id} t={t} />
      ))}
    </div>
  </div>
);

const ScholarshipTestimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const videoScrollRef = useRef<HTMLDivElement>(null);
  const [itemSize, setItemSize] = useState(0);
  const { t } = useTranslationHook();
  const SCHOLARSHIP_EDITIONS_DATA = getScholarshipEditionsData(t);
  const totalEditions = SCHOLARSHIP_EDITIONS_DATA.length;

  const allVideos = SCHOLARSHIP_EDITIONS_DATA.flatMap((edition) => edition.videos);
  const [mobileVideoIndex, setMobileVideoIndex] = useState(0);

  useEffect(() => {
    const updateItemSize = () => {
      const scrollEl = getScrollElement(scrollContainerRef.current);
      if (scrollEl) {
        setItemSize(scrollEl.clientWidth);
      }
    };

    updateItemSize();
    window.addEventListener("resize", updateItemSize);
    return () => window.removeEventListener("resize", updateItemSize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollEl = getScrollElement(scrollContainerRef.current);
      if (scrollEl) {
        const { scrollLeft, scrollWidth } = scrollEl;
        const cardWidth = scrollWidth / totalEditions;
        const index = Math.round(scrollLeft / cardWidth);
        setCurrentIndex(Math.min(Math.max(index, 0), totalEditions - 1));
      }
    };

    const scrollEl = getScrollElement(scrollContainerRef.current);
    if (scrollEl) {
      scrollEl.addEventListener("scroll", handleScroll);
      return () => scrollEl.removeEventListener("scroll", handleScroll);
    }
    return undefined;
  }, [totalEditions]);

  const scrollToEdition = useCallback(
    (index: number) => {
      const scrollEl = getScrollElement(scrollContainerRef.current);
      if (scrollEl) {
        const cardWidth = scrollEl.scrollWidth / totalEditions;
        scrollEl.scrollTo({ left: cardWidth * index, behavior: "smooth" });
      }
    },
    [totalEditions],
  );

  useEffect(() => {
    const scrollContainer = videoScrollRef.current?.querySelector(`.${styles.videoScroll}`);
    if (!scrollContainer) return undefined;

    const handleMobileScroll = () => {
      const cards = scrollContainer.children;
      if (!cards.length) return;
      const card = cards[0] as HTMLElement;
      const cardWidth = card.offsetWidth + 12;
      const index = Math.round(scrollContainer.scrollLeft / cardWidth);
      const videosPerEdition = 3;
      const editionIndex = Math.floor(index / videosPerEdition);
      setMobileVideoIndex(Math.min(Math.max(editionIndex, 0), totalEditions - 1));
    };

    scrollContainer.addEventListener("scroll", handleMobileScroll);
    return () => scrollContainer.removeEventListener("scroll", handleMobileScroll);
  }, [allVideos.length, totalEditions]);

  const scrollToMobileVideo = useCallback((editionIndex: number) => {
    const scrollContainer = videoScrollRef.current?.querySelector(`.${styles.videoScroll}`);
    if (!scrollContainer) return;
    const cards = scrollContainer.children;
    if (!cards.length) return;
    const card = cards[0] as HTMLElement;
    const cardWidth = card.offsetWidth + 12;
    const videosPerEdition = 3;
    scrollContainer.scrollTo({
      left: cardWidth * editionIndex * videosPerEdition,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className={styles.subsectionContainer}>
      <div className={styles.content}>
        {/* Desktop view: Scrollable editions */}
        <div className={styles.desktopView}>
          <div className={styles.fixedHeader}>
            <h2 className={styles.title}>{t("scholarship.testimonials.desktoptitle")}</h2>
            <p className={styles.subtitle}>{t("scholarship.testimonials.subtitle")}</p>
          </div>
          <div className={styles.scrollContainer} ref={scrollContainerRef}>
            <HorizontalScroll className={styles.horizontalScroll} showControls itemSize={itemSize}>
              {SCHOLARSHIP_EDITIONS_DATA.map((edition, index) => (
                <EditionCard key={index} edition={edition} t={t} />
              ))}
            </HorizontalScroll>

            <div className={styles.scrollIndicators}>
              {SCHOLARSHIP_EDITIONS_DATA.map((_, index) => (
                <div
                  key={index}
                  role="button"
                  tabIndex={0}
                  aria-label={`Go to edition ${index + 1}`}
                  className={`${styles.indicator} ${index === currentIndex ? styles.active : ""}`}
                  onClick={() => scrollToEdition(index)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") scrollToEdition(index);
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Mobile view: Fixed header + scrollable videos */}
        <div className={styles.mobileView}>
          <div className={styles.fixedHeader}>
            <h2 className={styles.title}>{t("scholarship.testimonials.mobiletitle")}</h2>
            <p className={styles.subtitle}>{t("scholarship.testimonials.subtitle")}</p>
          </div>

          <div className={styles.videoScrollContainer} ref={videoScrollRef}>
            <div className={styles.videoScroll}>
              {allVideos.map((video, index) => (
                <VideoCard key={index} videoId={video.id} t={t} />
              ))}
            </div>
          </div>

          <div className={styles.scrollIndicators}>
            {SCHOLARSHIP_EDITIONS_DATA.map((_, index) => (
              <div
                key={index}
                role="button"
                tabIndex={0}
                aria-label={`Go to edition ${index + 1}`}
                className={`${styles.indicator} ${index === mobileVideoIndex ? styles.active : ""}`}
                onClick={() => scrollToMobileVideo(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") scrollToMobileVideo(index);
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(ScholarshipTestimonials, {
  componentName: "ScholarshipTestimonials-ScholarshipDesktop",
  showForChina: true,
});
